import { ColorV2 } from "config/colors";
import { TimeReportResolution, TimeReportStatusV2 } from "model/TimeReport";
import type { TFunction } from "i18next";
import {
  getTimeReportAbsenceResolutionLabel,
  isTimeReportAbsenceResolution,
} from "web-apps/company/utils/absenceLabels";

interface ResolveTimeReportStatusBannerParams {
  status: TimeReportStatusV2 | null;
  resolution: TimeReportResolution | null;
  translate: TFunction;
}

interface TimeReportStatusBannerModel {
  iconName: string;
  label: string;
  textColor: ColorV2;
  backgroundColor: string;
}

const resolveResolutionLabel = (
  resolution: TimeReportResolution | null,
  translate: TFunction,
): string | null => {
  if (resolution === TimeReportResolution.WITHDRAWN) {
    return null;
  }

  if (resolution === TimeReportResolution.TIME_REPORTED) {
    return translate("CompanyTimeReport.TimeReported");
  }

  if (resolution != null && isTimeReportAbsenceResolution(resolution)) {
    return getTimeReportAbsenceResolutionLabel(resolution, translate);
  }

  return null;
};

type BannerVariant = "approved" | "withdrawn" | "toApprove" | "unreported";

const VARIANT_ICON: Record<BannerVariant, string> = {
  approved: "v2/checkmark",
  withdrawn: "v2/cross",
  toApprove: "v2/clock",
  unreported: "v2/document",
};

const toRgba = (hexColor: string, alpha: number): string => {
  const sanitizedHex = hexColor.replace("#", "");
  const red = Number.parseInt(sanitizedHex.slice(0, 2), 16);
  const green = Number.parseInt(sanitizedHex.slice(2, 4), 16);
  const blue = Number.parseInt(sanitizedHex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const resolveVariant = (
  status: TimeReportStatusV2 | null,
  resolution: TimeReportResolution | null,
): BannerVariant => {
  if (resolution === TimeReportResolution.WITHDRAWN) {
    return "withdrawn";
  }

  if (
    status === TimeReportStatusV2.APPROVED ||
    status === TimeReportStatusV2.CLOSED
  ) {
    return "approved";
  }

  if (status === TimeReportStatusV2.SUBMITTED) {
    return "toApprove";
  }

  return "unreported";
};

const resolveTextColor = (
  variant: BannerVariant,
  resolution: TimeReportResolution | null,
): ColorV2 => {
  if (variant === "withdrawn") {
    return ColorV2.NegativeRed;
  }

  if (
    variant === "approved" &&
    resolution != null &&
    isTimeReportAbsenceResolution(resolution)
  ) {
    return ColorV2.AbsentPurple;
  }

  if (variant === "approved") {
    return ColorV2.PositiveGreen;
  }

  if (variant === "toApprove") {
    return ColorV2.ElectricBlue;
  }

  return ColorV2.DarkAmbigiousYellow;
};

const resolveBackgroundColor = (
  variant: BannerVariant,
  resolution: TimeReportResolution | null,
): string => {
  if (variant === "withdrawn") {
    return toRgba(ColorV2.NegativeRed, 0.14);
  }

  if (
    variant === "approved" &&
    resolution != null &&
    isTimeReportAbsenceResolution(resolution)
  ) {
    return toRgba(ColorV2.AbsentPurple, 0.14);
  }

  if (variant === "approved") {
    return toRgba(ColorV2.PositiveGreen, 0.14);
  }

  if (variant === "toApprove") {
    return toRgba(ColorV2.ElectricBlue, 0.14);
  }

  return toRgba(ColorV2.DarkAmbigiousYellow, 0.16);
};

/** Resolve status-banner copy and styling for the time-report details panel. */
export const resolveTimeReportStatusBanner = (
  params: ResolveTimeReportStatusBannerParams,
): TimeReportStatusBannerModel => {
  const variant = resolveVariant(params.status, params.resolution);
  const textColor = resolveTextColor(variant, params.resolution);
  const backgroundColor = resolveBackgroundColor(variant, params.resolution);
  const statusLabel =
    variant === "approved"
      ? params.translate("TimeReports.StatusBanner.Approved")
      : variant === "withdrawn"
        ? params.translate("TimeReports.StatusBanner.Withdrawn")
        : variant === "toApprove"
          ? params.translate("TimeReports.ToApprove")
          : params.translate("TimeReports.StatusBanner.Unreported");
  const resolutionLabel = resolveResolutionLabel(
    params.resolution,
    params.translate,
  );
  const label =
    resolutionLabel == null
      ? statusLabel
      : `${statusLabel} - ${resolutionLabel}`;

  return {
    iconName: VARIANT_ICON[variant],
    label,
    textColor,
    backgroundColor,
  };
};
